import type { QueryClient } from '@tanstack/svelte-query';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { artistSectionQueryKey } from '#lib/websites/artists/api/getArtistSections.js';
import { getArtistPublishingBooksFilter } from '#lib/websites/artists/pages/ArtistDetailPage/utils/getArtistPublishingBooksFilter/getArtistPublishingBooksFilter.js';
import type { GetArtistPageQueryVariables } from '#lib/websites/artists/queries/__generated__/getArtistPage.generated.js';
import { GetArtistPageExhibitionsDocument } from '#lib/websites/artists/queries/__generated__/getArtistPageExhibitions.generated.js';
import { GetArtistPageNftsDocument } from '#lib/websites/artists/queries/__generated__/getArtistPageNfts.generated.js';
import { GetArtistPagePodcastsDocument } from '#lib/websites/artists/queries/__generated__/getArtistPagePodcasts.generated.js';
import { GetArtistPagePublishingBooksDocument } from '#lib/websites/artists/queries/__generated__/getArtistPagePublishingBooks.generated.js';
import type { GetArtistPageTalksQueryVariables } from '#lib/websites/artists/queries/__generated__/getArtistPageTalks.generated.js';
import { GetArtistPageTalksDocument } from '#lib/websites/artists/queries/__generated__/getArtistPageTalks.generated.js';
import { BooksSort } from '#lib/websites/publishing/constants/sort.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import type { ArtistsPageLoadEvent } from '$routes/artists/[slug]/types';

export const EXHIBITIONS_SORT = ['-Exhibitions_id.start_date'];
export const NFTS_SORT = ['-release_date'];
export const TALKS_SORT = ['-HENI_Talks_Talks_id.release_date'];
export const PODCASTS_SORT = [
	'-HENI_Talks_Podcast_Episode_id.publication_date',
];
// The /shop/books default order. The title key is a real tiebreaker: many
// books share a publication_date, so date alone leaves their order undefined.
export const PUBLISHING_BOOKS_SORT = BooksSort.Default.split(',');
export const ARTIST_VARIABLES = { offset: 0 };
export const ARTIST_PROJECTS_LIMIT = 8;

export const artistPageLoad = async ({
	parent,
	data,
}: ArtistsPageLoadEvent) => {
	const parentData = await parent();
	const queryClient: QueryClient = parentData.queryClient;
	const { artist } = data;

	// REST path. The server load already fetched the first page of every
	// section, so the caches are seeded under the REST keys the components use
	// and nothing is refetched. The pages are stored in the SAME envelope the
	// GraphQL pages use, which is what keeps the components' transforms,
	// getPageRows and getInfiniteData identical across both paths.
	if ('restArtistSlug' in data) {
		const slug = data.restArtistSlug as string;
		const params = { limit: ARTIST_PROJECTS_LIMIT, offset: 0 };
		const seed = (section: string, envelope: unknown) =>
			queryClient.setQueryData(artistSectionQueryKey(section, slug, params), {
				pageParams: [0],
				pages: [envelope],
			});

		seed('exhibitions', {
			Artists: [{ exhibitions: data.artist.exhibitions }],
		});
		seed('talks', { Artists: [{ talks: data.artist.talks }] });
		seed('podcasts', {
			Artists: [{ podcast_episodes: data.artist.podcast_episodes }],
		});
		seed('nfts', {
			Artists: [{ nft_collections: data.artist.nft_collections }],
		});
		seed('books', data.booksResponse);

		return { ...parentData, ...data };
	}

	const exhibitionsData = data.artist.exhibitions;
	const talksData = data.artist.talks;
	const podcastsData = data.artist.podcast_episodes;
	const nftsData = data.artist.nft_collections;

	const variables:
		GetArtistPageQueryVariables | GetArtistPageTalksQueryVariables = {
		offset: 0,
		filter: { slug: { _eq: artist.slug } },
		limit: ARTIST_PROJECTS_LIMIT,
	};

	// Books are queried from the Publishing_Books side (see
	// getArtistPagePublishingBooks), so they page with their own filter rather
	// than the Artists one shared by the other sections.
	const booksVariables = {
		offset: 0,
		filter: getArtistPublishingBooksFilter({
			slug: artist.slug,
			name: artist.name,
		}),
		limit: ARTIST_PROJECTS_LIMIT,
	};

	queryClient.setQueryData(
		getQueryKeys(GetArtistPageExhibitionsDocument, {
			...variables,
			sort: EXHIBITIONS_SORT,
		}),
		{
			pageParams: [0],
			pages: [
				{
					Artists: [
						{
							exhibitions: exhibitionsData,
						},
					],
				},
			],
		}
	);

	queryClient.setQueryData(
		getQueryKeys(GetArtistPageTalksDocument, {
			...variables,
			sort: TALKS_SORT,
		}),
		{
			pageParams: [0],
			pages: [
				{
					Artists: [
						{
							talks: talksData,
						},
					],
				},
			],
		}
	);

	queryClient.setQueryData(
		getQueryKeys(GetArtistPagePodcastsDocument, {
			...variables,
			sort: PODCASTS_SORT,
		}),
		{
			pageParams: [0],
			pages: [
				{
					Artists: [
						{
							podcast_episodes: podcastsData,
						},
					],
				},
			],
		}
	);

	queryClient.setQueryData(
		getQueryKeys(GetArtistPagePublishingBooksDocument, {
			...booksVariables,
			sort: PUBLISHING_BOOKS_SORT,
		}),
		{
			pageParams: [0],
			pages: [data.booksResponse],
		}
	);

	queryClient.setQueryData(
		getQueryKeys(GetArtistPageNftsDocument, {
			...variables,
			sort: NFTS_SORT,
		}),
		{
			pageParams: [0],
			pages: [
				{
					Artists: [
						{
							nft_collections: nftsData,
						},
					],
				},
			],
		}
	);

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetArtistPageExhibitionsDocument, {
			...variables,
			sort: EXHIBITIONS_SORT,
		}),
		initialPageParam: 0,
	});

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetArtistPageTalksDocument, {
			...variables,
			sort: TALKS_SORT,
		}),
		initialPageParam: 0,
	});

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetArtistPagePodcastsDocument, {
			...variables,
			sort: PODCASTS_SORT,
		}),
		initialPageParam: 0,
	});

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetArtistPagePublishingBooksDocument, {
			...booksVariables,
			sort: PUBLISHING_BOOKS_SORT,
		}),
		initialPageParam: 0,
	});

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetArtistPageNftsDocument, {
			...variables,
			sort: NFTS_SORT,
		}),
		initialPageParam: 0,
	});

	return {
		...parentData,
		...data,
		variables,
		booksVariables,
	};
};
